import {
  Suspense,
  type ComponentProps,
  type ComponentType,
  type FC,
} from 'react';
import { lazyWithRetry as lazyWithRetryBase } from '@doxyme/ui/utils';
import { NoSsr } from '../components/global/noSsr';
import { ChunkLoadErrorBoundary } from '../components/global/errorBoundary/chunkLoadErrorBoundary';
import { getLogger } from '../libs/logger';

const logger = getLogger('lazyWithRetry');

/**
 * Wraps a dynamic import with retry, telemetry, Suspense, an SSR guard, and a
 * ChunkLoadErrorBoundary that triggers a one-time full-page reload on
 * unrecoverable chunk failures. Call sites can render the returned component
 * directly without their own Suspense / NoSsr / boundary scaffolding.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  chunkName?: string,
): FC<ComponentProps<T>> => {
  const Lazy = lazyWithRetryBase(factory, {
    onSuccessAfterRetry: ({ attempts }) =>
      logger.info('lazy import succeeded after retry', {
        chunkName,
        attempts,
      }),
    onFinalFailure: ({ attempts, error }) =>
      logger.error('lazy import failed permanently', {
        chunkName,
        attempts,
        error,
      }),
  });

  const Wrapped: FC<ComponentProps<T>> = (props) => (
    <NoSsr>
      <ChunkLoadErrorBoundary>
        <Suspense fallback={null}>
          <Lazy {...(props as ComponentProps<typeof Lazy>)} />
        </Suspense>
      </ChunkLoadErrorBoundary>
    </NoSsr>
  );

  Wrapped.displayName = chunkName
    ? `LazyWithRetry(${chunkName})`
    : 'LazyWithRetry';

  return Wrapped;
};
